import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import CookiePreferencesModal from "@/components/CookiePreferencesModal";
import {
    ACCEPT_ALL,
    REJECT_ALL,
    bootConsent,
    fetchConsentConfig,
    getStoredConsent,
    saveConsent,
    logConsent,
    isSnoozed,
    snoozeConsent,
    SNOOZE_WINDOW_DAYS,
} from "@/lib/consent";

/**
 * CookieBanner (v2 — advanced compliance)
 *
 * Behavior
 * --------
 * • Boots Google Consent Mode v2 with all non-essential = denied on first import
 *   (via bootConsent) BEFORE any tag can fire.
 * • Renders only if no decision has been stored yet (localStorage key
 *   `md_cookie_consent_v2`). Never treats scrolling / outside-clicks / navigation
 *   as consent.
 * • Presents three equally-weighted actions: Reject all · Manage preferences ·
 *   Accept all — same size, same font, same border, same background.
 * • CCPA-region visitors also see a "Do Not Sell or Share My Personal
 *   Information" link inline; that link fires a REJECT_ALL with source=do-not-sell.
 * • Every decision is mirrored to `POST /api/consent/log` for the audit trail.
 * • A11y: role=dialog, aria-live=polite, focus-visible rings, keyboard-only usable.
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [cfg, setCfg] = useState({ region: "OTHER", ccpa_visible: false, requires_opt_in: false });

    useEffect(() => {
        // Screenshot/automation bypass (query param only, never persisted) —
        // lets internal tooling capture authenticated pages without the
        // consent modal blocking interaction. Real users never pass this param.
        if (new URLSearchParams(window.location.search).has("screenshot")) return;
        // Boot Google Consent Mode v2 defaults + apply any stored decision to PostHog etc.
        const stored = bootConsent();
        if (stored) return; // already decided, banner stays hidden
        if (isSnoozed()) return; // user hit "Save & customise later" — respect the 30-day window
        setVisible(true);
        fetchConsentConfig().then(setCfg);

        // Allow footer / support pages to reopen the preferences dialog on demand
        const onOpenPrefs = () => setPrefsOpen(true);
        window.addEventListener("md-open-cookie-preferences", onOpenPrefs);
        return () => window.removeEventListener("md-open-cookie-preferences", onOpenPrefs);
    }, []);

    const commit = (categories, source) => {
        const record = saveConsent({ categories, region: cfg.region, source });
        logConsent(record);
        setVisible(false);
        setPrefsOpen(false);
    };

    const handleSnooze = () => {
        snoozeConsent();
        setVisible(false);
    };

    if (!visible && !prefsOpen && getStoredConsent()) return null;

    return (
        <>
            {visible && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-live="polite"
                    aria-label="Cookie consent"
                    data-testid="cookie-banner"
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-sm"
                >
                    <div
                        className="w-full max-w-xl mx-auto rounded-2xl border border-border bg-card text-foreground shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6 sm:p-8 flex flex-col gap-5">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <Cookie className="w-6 h-6" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="overline text-primary">Your privacy · {cfg.region === "GDPR" ? "GDPR" : cfg.region === "CCPA" ? "CCPA" : "Global"}</div>
                                    <h2 className="text-lg sm:text-xl font-extrabold tracking-tight leading-snug mt-1">
                                        We only run the cookies you consent to.
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                        Necessary cookies power login and secure sessions — everything else (theme,
                                        analytics, marketing) waits for your OK. Manage each category individually, or
                                        pick a preset. Read our{" "}
                                        <Link to="/legal/cookies" className="text-primary underline font-semibold" data-testid="cookie-banner-policy-link">
                                            cookie notice
                                        </Link>.
                                    </p>
                                </div>
                            </div>

                            {/* Three equal-prominence buttons */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <EqualButton
                                    onClick={() => commit(REJECT_ALL, "banner")}
                                    label="Reject all"
                                    testId="cookie-banner-reject-btn"
                                />
                                <EqualButton
                                    onClick={() => setPrefsOpen(true)}
                                    label="Manage preferences"
                                    testId="cookie-banner-manage-btn"
                                />
                                <EqualButton
                                    onClick={() => commit(ACCEPT_ALL, "banner")}
                                    label="Accept all"
                                    testId="cookie-banner-accept-btn"
                                />
                            </div>

                            {cfg.ccpa_visible && (
                                <div className="text-center">
                                    <button
                                        onClick={() => commit(REJECT_ALL, "do-not-sell")}
                                        className="text-primary underline text-xs sm:text-sm font-semibold"
                                        data-testid="cookie-banner-do-not-sell"
                                    >
                                        Do Not Sell or Share My Personal Information
                                    </button>
                                </div>
                            )}

                            {/* Secondary escape hatch — defer the decision for 30 days.
                                Non-essential cookies stay denied while snoozed. */}
                            <div className="text-center flex flex-col items-center gap-1">
                                <button
                                    type="button"
                                    onClick={handleSnooze}
                                    className="text-primary underline text-xs sm:text-sm font-semibold hover:opacity-80"
                                    data-testid="cookie-banner-snooze-btn"
                                >
                                    Save &amp; customise later
                                </button>
                                <span className="text-[11px] text-muted-foreground" data-testid="cookie-banner-snooze-nudge">
                                    We&apos;ll remind you again in {SNOOZE_WINDOW_DAYS} days. Non-essential cookies stay off in the meantime.
                                </span>
                            </div>

                            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                                Scrolling or dismissing this banner does <b>not</b> imply consent — you must pick one of
                                the buttons above.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <CookiePreferencesModal
                open={prefsOpen}
                onClose={() => setPrefsOpen(false)}
                region={cfg.region}
                ccpaVisible={cfg.ccpa_visible}
                onDecision={() => setVisible(false)}
            />
        </>
    );
}

function EqualButton({ label, onClick, testId }) {
    // Same size, same weight, same border, same background — deliberately no
    // "primary" vs "secondary" hierarchy between Reject / Manage / Accept.
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="w-full h-11 rounded-lg border-2 border-primary bg-primary text-primary-foreground font-extrabold text-sm tracking-tight hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
            {label}
        </button>
    );
}
